import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardCheck, Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function JobChecklist({ requestId }) {
  const [checklist, setChecklist] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchChecklist = useCallback(async () => {
    try {
      const { data } = await API.get(`/checklists/job/${requestId}`);
      setChecklist(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const toggleItem = async (index) => {
    if (!checklist || checklist.is_locked) return;
    const item = checklist.items[index];
    try {
      await API.put(`/checklists/job/${requestId}/item/${index}`, {
        checked: !item.checked,
        notes: item.notes || "",
      });
      fetchChecklist();
    } catch (err) { toast.error("Failed to update"); }
  };

  const updateNotes = async (index, notes) => {
    try {
      await API.put(`/checklists/job/${requestId}/item/${index}`, {
        checked: checklist.items[index].checked,
        notes,
      });
    } catch { }
  };

  if (loading || !checklist) return null;

  const requiredDone = checklist.items.filter(i => i.required && i.checked).length;
  const requiredTotal = checklist.items.filter(i => i.required).length;

  return (
    <div className="space-y-2" data-testid="job-checklist">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-2.5 rounded-lg bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
        data-testid="checklist-toggle">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-purple-600" />
          <span className="text-xs font-semibold text-purple-800">
            Job Checklist
          </span>
          <Badge className={`text-[9px] ${checklist.progress >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"}`}>
            {Math.round(checklist.progress)}%
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-purple-500">{requiredDone}/{requiredTotal} required</span>
          {open ? <ChevronUp className="w-4 h-4 text-purple-400" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
        </div>
      </button>

      {open && (
        <Card className="border border-purple-200">
          <CardContent className="p-3 space-y-1">
            {/* Progress bar */}
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-2">
              <div className={`h-full rounded-full transition-all ${checklist.progress >= 100 ? "bg-emerald-500" : "bg-purple-500"}`}
                style={{ width: `${checklist.progress}%` }} />
            </div>

            {checklist.items.map((item, idx) => (
              <div key={idx}
                className={`flex items-start gap-2 p-2 rounded-lg transition-colors ${item.checked ? "bg-emerald-50" : "bg-white"}`}
                data-testid={`checklist-item-${idx}`}>
                <button onClick={() => toggleItem(idx)}
                  className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    item.checked ? "bg-emerald-500 border-emerald-500" : "border-zinc-300 hover:border-purple-400"
                  }`} data-testid={`check-item-${idx}`}>
                  {item.checked && <Check className="w-3 h-3 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs ${item.checked ? "line-through text-zinc-400" : "text-zinc-700"}`}>
                      {item.label}
                    </span>
                    {item.required && !item.checked && (
                      <span className="text-[8px] bg-red-100 text-red-600 px-1 rounded font-bold">REQ</span>
                    )}
                  </div>
                  {item.checked && (
                    <input
                      type="text"
                      defaultValue={item.notes}
                      onBlur={(e) => updateNotes(idx, e.target.value)}
                      placeholder="Add notes..."
                      className="mt-1 w-full px-2 py-1 bg-white border border-zinc-200 rounded text-[10px] text-zinc-500"
                    />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
